import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import Tooltip from '@material-ui/core/Tooltip';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import strings from 'lodash/string';
import { filter, orderBy } from 'lodash/collection';
import FileSaver from 'file-saver';
import InfiniteScroll from 'react-infinite-scroller';

import { getEntityType, httpGet, httpPost } from '../../utils';
import {
    AD_ENTITY_TYPES,
    DIRECTORY_TYPES,
    HTTP_OPTIONS,
    INSIGHTS_FEEDBACK_OPTIONS,
    INSIGHT_STATUSES
} from '@silverfort-nodelibs/common/utils/consts';
import { formatExtraDetailsData, riskLevelToText } from '@silverfort-nodelibs/common/utils/common';
import Loader from '../Loader/Loader';

import searchIcon from '../../common/Images/search.png';
import investigateIcon from '../../common/Images/investigation_blue.png';
import exportIcon from '../../common/Images/export_file.png';

import ExpandableDetails from '../ExpandableDetails';
import ConfirmationMessage from '../ConfirmationMessage';

import EntityNameDisplay from './EntityNameDisplay';
import EntityRow from './EntityRow';

import { getIconPathByRiskIndicator } from '@silverfort-nodelibs/common/utils/insight';
import { getIconFromIconPath } from '../../Insights/insights-utils';

import styles from '../EntitiesPopup.module.css';
import { useAppDrawer } from '../../AppDrawer/AppDrawerProvider';
import InvestigationDrawer from '../../InvestigationDrawer/InvestigationDrawer';

const RENDER_SIZE = 50;

const InsightStatusSelector = ({ insightStatusesToShow, setInsightStatusesToShow }) => {
    const [popupOpen, setPopupOpen] = useState(false);
    const popUpRef = useRef();

    useEffect(() => {
        const onClick = ({ target }) => !popUpRef.current?.contains(target) && setPopupOpen(false);
        if (popupOpen) {
            document.addEventListener('click', onClick);
        } else {
            document.removeEventListener('click', onClick);
        }
        return () => {
            document.removeEventListener('click', onClick);
        };
    }, [popupOpen]);

    const toggle = useCallback(
        (status) => {
            setInsightStatusesToShow((prev) => {
                const newObj = { ...prev };
                if (newObj[status]) {
                    delete newObj[status];
                } else {
                    newObj[status] = true;
                }
                return newObj;
            });
        },
        [setInsightStatusesToShow]
    );

    const insightStatusTostring = (status) => {
        return INSIGHTS_FEEDBACK_OPTIONS[status].filterLabel;
    };

    const insightStatusesToShowKeys = Object.keys(insightStatusesToShow);
    const currentFilterStr =
        insightStatusesToShowKeys.length > 0
            ? insightStatusesToShowKeys.length === Object.keys(INSIGHTS_FEEDBACK_OPTIONS).length
                ? 'All'
                : insightStatusesToShowKeys.flatMap(insightStatusTostring).join(', ')
            : 'None';

    return (
        <div>
            <div
                onClick={() => setPopupOpen((prev) => !prev)}
                className={`${styles.insightStatusSelectorBox} ${styles.insightStatusSelectorText}`}>
                <div>
                    <span style={{ color: '#188ded' }}>Show: </span> {currentFilterStr}
                </div>
                <i className={`fa ${popupOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ color: '#188ded' }} />
            </div>
            {popupOpen && (
                <div
                    className={`${styles.insightStatusSelectorPopup} ${styles.insightStatusSelectorText}`}
                    ref={popUpRef}>
                    {Object.values(INSIGHTS_FEEDBACK_OPTIONS).map((option) => {
                        return (
                            <div
                                className={styles.insightStatusOption}
                                onClick={() => toggle(option.status)}
                                key={option.status}>
                                <input
                                    type="checkbox"
                                    checked={insightStatusesToShow[option.status] !== undefined}
                                    readOnly
                                />
                                {`${option.filterLabel.toLowerCase()}`}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const EntitiesPopup = (props) => {
    const [insightStatusesToShow, setInsightStatusesToShow] = useState({ [INSIGHT_STATUSES.ACTIVE]: true });
    const [isLoading, setIsLoading] = useState(true);
    const [elemState, setElemState] = useState({
        queryFilter: '',
        entitiesData: [],
        lastIndex: 0,
        entitiesToRemove: [],
        updateStatistics: false,
        isAllMarked: false
    });
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [confirmationModalState, setConfirmationModalState] = useState({
        show: false,
        text: '',
        status: INSIGHT_STATUSES.ACTIVE
    });
    const { closeAppDrawer, displayContent } = useAppDrawer();

    useEffect(() => {
        return closeAppDrawer();
    }, []);

    const getNameByMapping = useCallback(
        (rawName) => {
            if (props.guidToInfoMapping && props.guidToInfoMapping[rawName]) {
                if (props.entityType === AD_ENTITY_TYPES.SERVER || props.isTopEntities) {
                    return props.guidToInfoMapping[rawName].name || props.guidToInfoMapping[rawName].identifier;
                } else if (props.entityType === AD_ENTITY_TYPES.USER) {
                    return props.guidToInfoMapping[rawName].identifier || props.guidToInfoMapping[rawName].name;
                }
            }
            return rawName;
        },
        [props.guidToInfoMapping, props.entityType, props.isTopEntities]
    );

    const loadInsights = useCallback(
        async (url) => {
            let entitiesData = [];

            setIsLoading(true);
            try {
                const detailsResponse = await httpGet(url);
                const details = detailsResponse.data;

                const addedGuids = new Set(details.insightsDetailsAdded);

                for (const entity of details.insightsDetails) {
                    if (props.entityType === AD_ENTITY_TYPES.USER && entity.identifier) {
                        entity.name = entity.identifier;
                    }
                    if (props.entityType === AD_ENTITY_TYPES.APPLICATION || props.entityType === AD_ENTITY_TYPES.OKTA) {
                        entity.name = entity.display_name;
                        entity.guid = entity.identifier;
                    }

                    if (addedGuids.has(entity.guid)) {
                        entity.isNew = true;
                    }

                    entitiesData.push(entity);
                }

                entitiesData = orderBy(entitiesData, ['name'], ['asc']);
            } catch (err) {
                console.error(err);
            }
            setElemState((prev) => {
                return { ...prev, entitiesData };
            });
            setIsLoading(false);
        },
        [props.entityType]
    );

    useEffect(() => {
        let entitiesData = [];
        if (props.data && props.data.length > 0) {
            for (let index = 0; index < Math.min(props.data.length, 5000); index++) {
                let entityData = props.data[index];
                if (props.isCountObject || props.isNameObject) {
                    entityData.guid = entityData.name;
                    entityData.name = getNameByMapping(entityData.name);
                } else if (props.isTopEntities) {
                    entityData.risk = riskLevelToText(entityData.riskLevel);
                } else {
                    entityData = { guid: entityData, name: getNameByMapping(entityData) };
                }

                entitiesData.push(entityData);
            }

            if (!props.isCountObject && !props.isTopEntities) {
                entitiesData = orderBy(entitiesData, ['name'], ['asc']);
            }
            setElemState((prev) => {
                return { ...prev, entitiesData: entitiesData };
            });
            setIsLoading(false);
        } else if (!props.data && props.loadUrl) {
            loadInsights(props.loadUrl);
        } else {
            setElemState((prev) => {
                return { ...prev, entitiesData: entitiesData };
            });
            setIsLoading(false);
        }
    }, [
        props.data,
        props.isCountObject,
        props.isNameObject,
        props.isTopEntities,
        props.loadUrl,
        getNameByMapping,
        loadInsights
    ]);

    const formatIsNewAndInsightStatus = useCallback(
        (entity) => {
            let formattedStr = '';
            if (props.daysBackForNew) {
                const isNew = entity.isNew ? 'yes' : 'no';
                formattedStr += `,${isNew}`;
            }
            const insight_status = INSIGHTS_FEEDBACK_OPTIONS[entity.insight_status]?.filterLabel.toLowerCase();
            formattedStr += insight_status ? `,${insight_status}` : ',';
            return formattedStr;
        },
        [props.daysBackForNew]
    );

    const exportData = useCallback(
        (entitiesToExport) => {
            const fileName = `silverfort_${strings.snakeCase(props.title)}.csv`;

            let csvData = '';
            const isInsight = !!entitiesToExport[0].insight_status;
            const isNewHeaderStr = props.daysBackForNew ? `,Is New (${props.daysBackForNew}d)` : '';
            const isNewHeaderAndInsightStatusStr = `${isNewHeaderStr}${isInsight ? ',Insight status' : ','}`;
            if (props.isTopEntities) {
                csvData += `Name,Risk,Domain Name${isNewHeaderAndInsightStatusStr}\n`;
                for (const entity of entitiesToExport) {
                    csvData += `${entity.name ?? entity.display_name},${entity.risk},${
                        entity.domainName
                    }${formatIsNewAndInsightStatus(entity)}\n`;
                }
            } else if (props.isCountObject) {
                csvData += `Name,Requests${isNewHeaderAndInsightStatusStr}\n`;
                for (const entity of entitiesToExport) {
                    csvData += `${entity.name ?? entity.display_name},${entity.count}${formatIsNewAndInsightStatus(
                        entity
                    )}\n`;
                }
            } else if (props.extraEntityDetails) {
                csvData += `Name`;
                for (const column of props.extraEntityDetails) {
                    csvData += `,${column.moreDetailsHeader}`;
                }
                csvData += `${isNewHeaderAndInsightStatusStr}\n`;
                for (const entity of entitiesToExport) {
                    csvData += `${entity.name ?? entity.display_name}`;
                    for (const column of props.extraEntityDetails) {
                        const moreDetailsData = formatExtraDetailsData(entity, column, true);
                        csvData += `,${moreDetailsData}`;
                    }
                    csvData += `${formatIsNewAndInsightStatus(entity)}\n`;
                }
            } else if (props.isApplicationObject) {
                csvData += `Name,Cloud Type\n`;
                const type =
                    props.entityType === AD_ENTITY_TYPES.APPLICATION
                        ? DIRECTORY_TYPES.aad.displayName
                        : DIRECTORY_TYPES.okta.displayName;
                for (const entity of entitiesToExport) {
                    csvData += `${entity.name ?? entity.display_name},${entity.type ? entity.type : type}\n`;
                }
            } else {
                csvData = `${props.title}${isNewHeaderAndInsightStatusStr}\n${entitiesToExport
                    .map((entity) => `${entity.name ?? entity.display_name}${formatIsNewAndInsightStatus(entity)}`)
                    .join('\n')}`;
            }

            const blob = new Blob([csvData], { type: 'text/plain;charset=utf-8' });
            FileSaver.saveAs(blob, fileName);
        },
        [
            props.title,
            props.daysBackForNew,
            props.isTopEntities,
            props.isCountObject,
            props.extraEntityDetails,
            props.isApplicationObject,
            props.entityType,
            formatIsNewAndInsightStatus
        ]
    );

    const onSelectEntity = useCallback(
        (isChecked, entityData) => {
            const updatedEntities = [...elemState.entitiesToRemove];
            if (isChecked) {
                updatedEntities.push(entityData);
            } else {
                const itemIndex = updatedEntities.findIndex((entity) => entityData.guid === entity.guid);
                if (itemIndex !== -1) {
                    updatedEntities.splice(itemIndex, 1);
                }
            }
            setElemState((prev) => {
                return { ...prev, entitiesToRemove: updatedEntities };
            });
        },
        [elemState.entitiesToRemove]
    );

    const removeSelectedEntitiesInsights = async (newStatus) => {
        setConfirmationModalState({
            show: false,
            text: '',
            status: INSIGHT_STATUSES.ACTIVE
        });
        if (elemState.entitiesToRemove.length > 0) {
            const removeEntitiesInsightsData = {
                entityType: props.entityType,
                ruleName: props.insightName,
                entities: elemState.entitiesToRemove
            };

            const removeEntityInsightsResponse = await httpPost(
                '/Insights/updateEntityInsight',
                {
                    data: removeEntitiesInsightsData,
                    newStatus
                },
                { [HTTP_OPTIONS.HANDLE_ERRORS]: true }
            );

            if (
                removeEntityInsightsResponse.data?.successEntities &&
                removeEntityInsightsResponse.data?.successEntities.length !== elemState.entitiesToRemove.length
            ) {
                props.onErrorRemovingEntitiesFromInsights(
                    'Error',
                    'The action did not complete normally, and not all selected insights were hidden. Retry the action for those still appearing in the list.'
                );
            }

            if (props.loadUrl) {
                loadInsights(props.loadUrl);
                setElemState((prev) => {
                    return { ...prev, entitiesToRemove: [], updateStatistics: true };
                });
            }
        }
    };

    useEffect(() => {
        const checkMarkedEntitiesExist = () => {
            const entitiesGuids = new Set(elemState.entitiesData.map((entity) => entity.guid));
            setElemState((elemState) => {
                const entitiesToRemove = elemState.entitiesToRemove.filter((entity) => entitiesGuids.has(entity.guid));
                return { ...elemState, entitiesToRemove: entitiesToRemove };
            });
        };
        const filterEntities = () => {
            let tmpFilteredEntities = props.isWithFilter
                ? elemState.entitiesData.filter((elem) => insightStatusesToShow[elem.insight_status])
                : elemState.entitiesData;
            if (elemState.queryFilter !== '' && tmpFilteredEntities.length > 0) {
                if (elemState.queryFilter.startsWith('!')) {
                    const query = elemState.queryFilter.substr(1).toLowerCase();
                    tmpFilteredEntities = filter(tmpFilteredEntities, (entity) => {
                        const fullEntityName = `${entity.name ?? entity.display_name}${entity.isNew ? ' (new)' : ''}`;
                        return !fullEntityName.toLowerCase().includes(query);
                    });
                } else {
                    const query = elemState.queryFilter.toLowerCase();
                    tmpFilteredEntities = filter(tmpFilteredEntities, (entity) => {
                        const fullEntityName = `${entity.name ?? entity.display_name}${entity.isNew ? ' (new)' : ''}`;
                        return fullEntityName.toLowerCase().includes(query);
                    });
                }
            }
            setFilteredEntities(tmpFilteredEntities);
        };
        filterEntities();
        checkMarkedEntitiesExist();
    }, [elemState.entitiesData, elemState.queryFilter, insightStatusesToShow, props.isWithFilter]);

    useEffect(() => {
        if (!props.allowMarkAll) {
            return;
        }
        const guidsToRemove = new Set(elemState.entitiesToRemove.map((entity) => entity.guid));
        const renderedEntities = filteredEntities.slice(0, elemState.lastIndex + RENDER_SIZE);
        const isAllMarked = renderedEntities.every((entity) => guidsToRemove.has(entity.guid));
        setElemState((elemState) => {
            return { ...elemState, isAllMarked: isAllMarked };
        });
    }, [filteredEntities, elemState.lastIndex, elemState.entitiesToRemove, props.allowMarkAll]);

    const markAllChanged = (isChecked) => {
        if (isChecked) {
            const renderedEntities = filteredEntities.slice(0, elemState.lastIndex + RENDER_SIZE);
            setElemState((elemState) => {
                const guidsToRemove = new Set(elemState.entitiesToRemove.map((entity) => entity.guid));
                renderedEntities
                    .filter((entity) => !guidsToRemove.has(entity.guid))
                    .forEach((entity) => elemState.entitiesToRemove.push(entity));
                return { ...elemState, entitiesToRemove: [...elemState.entitiesToRemove] };
            });
        } else {
            const renderedGuids = new Set(
                filteredEntities.slice(0, elemState.lastIndex + RENDER_SIZE).map((entity) => entity.guid)
            );
            setElemState((elemState) => {
                elemState.entitiesToRemove = elemState.entitiesToRemove.filter(
                    (entity) => !renderedGuids.has(entity.guid)
                );
                return { ...elemState, entitiesToRemove: [...elemState.entitiesToRemove] };
            });
        }
    };

    const entitiesRows = [];

    const numOfEntities = filteredEntities ? filteredEntities.length : 0;
    const hasMore = elemState.lastIndex + RENDER_SIZE < filteredEntities.length;
    const filteredEntitiesData = filteredEntities.slice(0, elemState.lastIndex + RENDER_SIZE);
    const investigateClasses = `investigate`;

    let index = 1;
    for (const entityData of filteredEntitiesData) {
        const guidsToRemove = new Set(elemState.entitiesToRemove.map((entity) => entity.guid));
        if (props.isCountObject) {
            entitiesRows.push(
                <div className="entityRow entityData" key={'entityRow_' + index}>
                    <div className="rowIndex noSelection">{`${index}.`}</div>
                    <EntityNameDisplay entityData={entityData} />
                    <div className="count" title={entityData.count}>
                        {entityData.count}
                    </div>
                </div>
            );
        } else if (props.extraEntityDetails) {
            const columns = [];
            for (const column of props.extraEntityDetails) {
                const moreDetailsData = formatExtraDetailsData(entityData, column);
                columns.push(
                    <div key={'entityCol_' + index} className="details" title={moreDetailsData}>
                        {moreDetailsData}
                    </div>
                );
            }
            entitiesRows.push(
                <div className="entityRow entityData" key={'entityRow_' + index}>
                    {props.allowRemoveEntities && (
                        <input
                            readOnly
                            checked={guidsToRemove.has(entityData.guid)}
                            type="checkbox"
                            id={`entityCheckBox_${index}`}
                            onChange={(e) => {
                                onSelectEntity(e.target.checked, entityData);
                            }}
                        />
                    )}
                    <div className={`rowIndex ${!props.allowRemoveEntities ? 'noSelection' : ''}`}>{`${index}.`}</div>
                    <EntityNameDisplay entityData={entityData} />
                    {columns}
                    {props.jumpToInvestigation && (
                        <div
                            className={investigateClasses}
                            title={'Investigate'}
                            onClick={() =>
                                displayContent(
                                    <InvestigationDrawer
                                        guid={entityData.guid}
                                        adType={getEntityType(props.entityType, entityData)}
                                    />
                                )
                            }>
                            <img src={investigateIcon} alt="" />
                        </div>
                    )}
                </div>
            );
        } else if (props.isTopEntities) {
            entitiesRows.push(
                <div className="entityRow entityData" key={'entityRow_' + index}>
                    <div className="rowIndex noSelection">{`${index}.`}</div>
                    <EntityNameDisplay entityData={entityData} />
                    <div className="risk" title={entityData.risk}>
                        {entityData.risk}
                    </div>
                    <div className="details" title={entityData.domainName}>
                        {entityData.domainName}
                    </div>
                    {props.jumpToInvestigation && (
                        <div
                            className={investigateClasses}
                            title={'Investigate'}
                            onClick={() =>
                                displayContent(
                                    <InvestigationDrawer
                                        guid={entityData.guid}
                                        adType={getEntityType(props.entityType, entityData)}
                                    />
                                )
                            }>
                            <img src={investigateIcon} alt="" />
                        </div>
                    )}
                </div>
            );
        } else {
            entitiesRows.push(
                <EntityRow
                    key={'entityRow_' + index}
                    index={index}
                    entityData={entityData}
                    allowRemoveEntities={props.allowRemoveEntities}
                    isApplicationObject={props.isApplicationObject}
                    jumpToInvestigation={props.jumpToInvestigation}
                    investigateClasses={investigateClasses}
                    entityType={props.entityType}
                    jumpToPolicy={props.jumpToPolicy}
                    ExtraDataComponent={props.ExtraDataComponent}
                    checked={guidsToRemove.has(entityData.guid)}
                    onSelectEntity={onSelectEntity}
                    explainabilityTitle={props.explainabilityTitle}
                />
            );
        }
        index++;
    }

    const headers = [];
    if (props.extraEntityDetails) {
        index = 1;
        for (const column of props.extraEntityDetails) {
            headers.push(
                <div key={'entityHeader_' + index} className="details">
                    {column.moreDetailsHeader}
                </div>
            );
            index++;
        }
    }
    return (
        <>
            <Modal
                id="entitiesPopupModal"
                dialogClassName="modal-lg"
                show={props.show}
                onHide={() => props.onHide(elemState.updateStatistics)}
                keyboard={true}
                backdrop="static">
                <Modal.Header>
                    <Modal.Title id="modalTitleId" className={styles.modalHeader}>
                        <div className="leftSide">
                            <div className="popupTitle" title={props.title}>
                                {props.title}
                            </div>
                            <span>({numOfEntities})</span>
                            {props.tooltip && (
                                <Tooltip
                                    title={<div>{props.tooltip}</div>}
                                    enterDelay={100}
                                    placement={'right'}
                                    classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                    <div className="moreInfoIcon"></div>
                                </Tooltip>
                            )}
                        </div>
                        <div className={styles.rightSide}>
                            {props.isWithFilter && (
                                <InsightStatusSelector
                                    insightStatusesToShow={insightStatusesToShow}
                                    setInsightStatusesToShow={setInsightStatusesToShow}
                                />
                            )}
                            <div className="searchInsights">
                                <img src={searchIcon} alt="" />
                                <input
                                    type="text"
                                    className="form-control"
                                    name="SearchQuery"
                                    placeholder="Search..."
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setElemState((prev) => {
                                            return { ...prev, queryFilter: value, lastIndex: 0 };
                                        });
                                    }}
                                    value={elemState.queryFilter}
                                />
                                <Button
                                    className="clearSearchButton"
                                    onClick={() =>
                                        setElemState((prev) => {
                                            return { ...prev, queryFilter: '', lastIndex: 0 };
                                        })
                                    }>
                                    X
                                </Button>
                            </div>
                        </div>
                    </Modal.Title>
                    {(props.description || props.mitigation) && (
                        <div className="extraDatas">
                            {props.description && (
                                <ExpandableDetails
                                    expandableText={props.description}
                                    titleText={'Description'}
                                    key={'Description'}
                                />
                            )}
                            {props.mitigation && (
                                <ExpandableDetails
                                    expandableText={props.mitigation}
                                    titleText={'Mitigation'}
                                    key={'Mitigation'}
                                />
                            )}
                            {props.reference && (
                                <ExpandableDetails
                                    expandableText={props.reference}
                                    titleText={'Reference'}
                                    key={'Reference'}
                                    showLinks
                                />
                            )}
                        </div>
                    )}
                </Modal.Header>
                <Modal.Body>
                    {isLoading && <Loader />}
                    {!isLoading && entitiesRows && entitiesRows.length > 0 && (
                        <div className="entitiesTable">
                            <div
                                className={
                                    props.isTopEntities
                                        ? 'header topEntities'
                                        : props.allowMarkAll
                                        ? 'header markAll'
                                        : 'header'
                                }>
                                {props.allowMarkAll && (
                                    <div className="markAll">
                                        <input
                                            readOnly
                                            checked={elemState.isAllMarked}
                                            type="checkbox"
                                            id={`entityCheckBox_${index}`}
                                            onChange={(e) => {
                                                markAllChanged(e.target.checked);
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="rowIndex">#</div>
                                <div className="name">Name</div>
                                {props.isCountObject && <div className="count">Requests</div>}
                                {props.extraEntityDetails && headers}
                                {props.isTopEntities && <div className="risk">Risk</div>}
                                {props.isTopEntities && <div className="details">Domain Name</div>}
                            </div>
                            <div className="entities light-scrollbar">
                                <InfiniteScroll
                                    loadMore={() =>
                                        setElemState((prev) => {
                                            return { ...prev, lastIndex: prev.lastIndex + RENDER_SIZE };
                                        })
                                    }
                                    hasMore={hasMore}
                                    initialLoad={false}
                                    useWindow={false}>
                                    {entitiesRows}
                                </InfiniteScroll>
                            </div>
                        </div>
                    )}
                    {!isLoading && (!entitiesRows || entitiesRows.length === 0) && (
                        <div className="empty">
                            <span>Nothing to show</span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className={'footerContainer' + (props.allowRemoveEntities ? ' allowRemove' : '')}>
                        {props.allowRemoveEntities && (
                            <div className="numChosen" key={'numChosen'}>
                                <span>{elemState.entitiesToRemove.length} Selected</span>
                            </div>
                        )}
                        {props.allowRemoveEntities &&
                            elemState.entitiesToRemove &&
                            elemState.entitiesToRemove.length > 0 && (
                                <DropdownButton
                                    className={styles.feedbackDropdown}
                                    dropup
                                    noCaret
                                    title={
                                        <div>
                                            Insight Feedback Selection <i className={`fa fa-angle-up`}></i>
                                        </div>
                                    }
                                    id={'operations-dropdown'}>
                                    {Object.values(INSIGHTS_FEEDBACK_OPTIONS).map((option, index) => {
                                        return (
                                            <>
                                                <MenuItem key={`feedbackMenuItem_${index}`}>
                                                    <span
                                                        key={`feedbackMenuItemSpan_${index}`}
                                                        className={styles.dropdownItem}
                                                        onClick={() =>
                                                            setConfirmationModalState({
                                                                show: true,
                                                                text: option.confirmationText,
                                                                status: option.status
                                                            })
                                                        }>
                                                        <span
                                                            className={styles.dropdownItemLabel}
                                                            key={`feedbackMenuItemLabel_${index}`}>
                                                            {option.label}
                                                        </span>
                                                        <span
                                                            className={styles.dropdownItemDescription}
                                                            key={`feedbackMenuItemDesc_${index}`}>
                                                            {option.description}
                                                        </span>
                                                    </span>
                                                </MenuItem>
                                                {Object.values(INSIGHTS_FEEDBACK_OPTIONS).length - 1 !== index && (
                                                    <MenuItem key={`feedbackMenuItemDivider_${index}`} divider />
                                                )}
                                            </>
                                        );
                                    })}
                                </DropdownButton>
                            )}

                        {props.ExportDataComponent ? (
                            <div
                                className={
                                    'export' +
                                    (entitiesRows && entitiesRows.length > 0 ? '' : ' disabled') +
                                    (props.allowRemoveEntities ? ' right' : '')
                                }>
                                <props.ExportDataComponent
                                    exportDisplayedData={() =>
                                        exportData(filteredEntities)
                                    }></props.ExportDataComponent>
                            </div>
                        ) : (
                            <div
                                key="exportButton"
                                className={
                                    'export' +
                                    (entitiesRows && entitiesRows.length > 0 ? '' : ' disabled') +
                                    (props.allowRemoveEntities ? ' right' : '')
                                }
                                onClick={() => exportData(filteredEntities)}>
                                <img src={exportIcon} alt="" />
                                Export To CSV
                            </div>
                        )}
                        <Button
                            className={'btn btn-success' + (props.allowRemoveEntities ? ' right' : '')}
                            onClick={() => props.onHide(elemState.updateStatistics)}>
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
            {confirmationModalState.show && (
                <ConfirmationMessage
                    title={'Are you sure?'}
                    body={confirmationModalState.text}
                    approveText={'Yes'}
                    cancelText={'No'}
                    OnApprove={() => removeSelectedEntitiesInsights(confirmationModalState.status)}
                    OnCancel={() =>
                        setConfirmationModalState({ show: false, text: '', status: INSIGHT_STATUSES.ACTIVE })
                    }
                    OnHide={() => setConfirmationModalState({ show: false, text: '', status: INSIGHT_STATUSES.ACTIVE })}
                    style={styles.confirmationMessage}
                />
            )}
        </>
    );
};

class RiskIndicatorsPopup extends React.Component {
    render() {
        const tableRows = [];
        if (this.props.data.length > 0) {
            for (const [index, reason] of this.props.data.entries()) {
                const indicatorIcon = getIconFromIconPath(getIconPathByRiskIndicator(reason));
                const indicatorText = reason.entityTitle;
                const tooltip = reason.tooltip;

                tableRows.push(
                    <div key={'risk_indicator_' + index} className="riskIndicator">
                        <div className="imageContainer">
                            <img src={indicatorIcon} alt="" />
                        </div>
                        <span>{indicatorText}</span>
                        {tooltip && (
                            <Tooltip
                                title={<div>{tooltip}</div>}
                                enterDelay={100}
                                placement={'right'}
                                classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                <div className="moreInfoIcon"></div>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        }

        return (
            <Modal
                id="entitiesPopupModal"
                dialogClassName="modal-lg"
                show={this.props.show}
                onHide={() => this.props.onHide()}
                keyboard={true}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div className="leftSide">
                            Risk Indicators<span>({tableRows ? tableRows.length : 0})</span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tableRows && tableRows.length > 0 && (
                        <div className="entitiesTable">
                            <div className="entities">{tableRows}</div>
                        </div>
                    )}
                    {(!tableRows || tableRows.length === 0) && (
                        <div className="empty">
                            <span>Nothing to show</span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={() => this.props.onHide()}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export { EntitiesPopup, RiskIndicatorsPopup };
